import { getElementSchemaFromArraySchema } from './get_element_schema_from_array_schema.js';
import { getObjectSchemaFromSubtype } from './get_object_schema_from_subtype.js';
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
// Same rules as the write-side validator (SchemaInflator.validateObject), so a field the editor
// hides is never reported. It ignores `orConditions` too.
function satisfiesConditions(fieldSchema, object) {
    return (fieldSchema.conditions ?? []).every((condition) => {
        if (condition.model) {
            return true;
        }
        if ('exists' in condition) {
            return !!object[condition.field];
        }
        const actualValue = object[condition.field];
        if (Array.isArray(actualValue)) {
            // Only an empty array can match, and only an array in `values`.
            return !actualValue.length && condition.values.some(Array.isArray);
        }
        return condition.values.some((acceptableValue) => actualValue === acceptableValue);
    });
}
// The fields an object value follows. A freeform object has none to check.
function getObjectFieldSchemaByName(fieldSchema) {
    if (fieldSchema.type !== 'object') {
        return undefined;
    }
    if ('fields' in fieldSchema) {
        return fieldSchema.fields;
    }
    if ('subtype' in fieldSchema) {
        return getObjectSchemaFromSubtype(fieldSchema.subtype).fields;
    }
    return undefined;
}
function collectMissingRequiredFields(fieldSchemaByName, object, parentPath, missingFields) {
    for (const [fieldName, fieldSchema] of Object.entries(fieldSchemaByName)) {
        if (!satisfiesConditions(fieldSchema, object)) {
            continue;
        }
        const path = parentPath ? `${parentPath}.${fieldName}` : fieldName;
        if (!Object.hasOwn(object, fieldName)) {
            if (fieldSchema.required) {
                missingFields.push({ path, fieldSchema });
            }
            continue;
        }
        const value = object[fieldName];
        if (fieldSchema.type === 'object') {
            const objectFieldSchemaByName = getObjectFieldSchemaByName(fieldSchema);
            if (objectFieldSchemaByName && isRecord(value)) {
                collectMissingRequiredFields(objectFieldSchemaByName, value, path, missingFields);
            }
        }
        else if (fieldSchema.type === 'array' && Array.isArray(value)) {
            const elementFieldSchemaByName = getObjectFieldSchemaByName(getElementSchemaFromArraySchema(fieldSchema));
            if (elementFieldSchemaByName) {
                value.forEach((element, index) => {
                    if (isRecord(element)) {
                        collectMissingRequiredFields(elementFieldSchemaByName, element, `${path}.${index}`, missingFields);
                    }
                });
            }
        }
        else if (fieldSchema.type === 'map' && isRecord(value)) {
            Object.entries(value).forEach(([key, mapValue]) => {
                if (isRecord(mapValue)) {
                    collectMissingRequiredFields(fieldSchema.fields, mapValue, `${path}.${key}`, missingFields);
                }
            });
        }
    }
}
/**
 * Required fields, with their dot-paths, that are absent from `value` although their `conditions`
 * hold. Descends into present objects, object array elements and map values, including subtyped
 * ones; never into `null`, freeform objects, or absent parents.
 * Presence only — `null` counts as present and no type is checked.
 */
export function findMissingRequiredFields(fieldSchemaByName, value) {
    if (!isRecord(value)) {
        return [];
    }
    const missingFields = [];
    collectMissingRequiredFields(fieldSchemaByName, value, '', missingFields);
    return missingFields;
}
//# sourceMappingURL=find_missing_required_fields.js.map